import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock, MapPin, SlidersHorizontal, Snowflake, Sparkles } from "lucide-react";
import SiteShell from "../components/layout/SiteShell";
import ContentPageLayout from "../components/content/ContentPageLayout";
import PremiumPlaceholder from "../components/content/PremiumPlaceholder";
import SEO from "../components/seo/SEO";
import ConversionCtas from "../components/content/ConversionCtas";
import { buildFaqPageJsonLd } from "../lib/seoJsonLd";

const faqs = [
  {
    question: "Do you ship outside the UK?",
    answer:
      "Not at this stage. MossMatrix ships within the United Kingdom only. International delivery is not available yet. Join the mailing list if you want a heads-up when that changes.",
  },
  {
    question: "How long will my order take to prepare?",
    answer:
      "Fresh gel is usually prepared within about 1–3 working days from order to dispatch, then sent on UK tracked delivery. At peak times we may need the full window, and kitchen scheduling can take up to 48 hours after checkout before your batch is slotted. We batch carefully because the product is fresh.",
  },
  {
    question: "Why order from your website instead of a marketplace?",
    answer:
      "The best MossMatrix prices are on this site. We may sell through marketplaces sometimes, but fees and delivery rules there can change what you pay. Ordering here keeps things simple and supports small-batch prep.",
  },
  {
    question: "What is sea moss gel?",
    answer:
      "Sea moss gel is dried sea moss soaked, cleaned, and blended with spring water into a smooth spoonable gel. Many people stir a spoonful into drinks, oats, herbal tea, or simple recipes as part of everyday eating.",
  },
  {
    question: "What is the difference between Gold and Purple Sea Moss?",
    answer:
      "Both are wildcrafted MossMatrix bases. Gold is usually smooth and mild. Purple has a deeper colour and a bolder natural profile, and we price it as our premium, higher-priced base. It is a taste and variety choice, not a medical strength contest.",
  },
  {
    question: "How much should I take?",
    answer:
      "Start small and adjust to taste. Many people use about one to two spoonfuls in a drink or bowl. That is a routine suggestion, not medical advice. For personal advice, speak with a qualified professional.",
  },
  {
    question: "How long does a jar last?",
    answer:
      "Refrigerated and handled with clean utensils, most customers finish a jar within a few weeks. Timing depends on your fridge, how often you open the jar, and how much you use.",
  },
  {
    question: "Does it need to be refrigerated?",
    answer:
      "Yes. Treat MossMatrix gel like fresh food. Refrigerate after it arrives and keep the jar sealed between uses.",
  },
  {
    question: "Can I freeze sea moss gel?",
    answer:
      "You can freeze portions in freezer-safe pouches, tubs, silicone trays, or heavy-duty zip bags. Label the date. Thaw overnight in the fridge before use.",
  },
  {
    question: "Can I freeze it in glass?",
    answer:
      "No. Liquid expands when frozen and glass jars can crack. Decant into plastic tubs or bags first.",
  },
  {
    question: "What can I mix it with?",
    answer:
      "Cooled herbal tea, oats, plant milks, and simple sauces are popular. Fruit can be mixed in at home if you like, but we do not ship fruit or vegetables in gel jars because they ferment quickly in transit.",
  },
  {
    question: "Is Moss Matrix for gym routines?",
    answer:
      "Moss Matrix is a food product, not a performance supplement. Many customers add sea moss gel to smoothies, oats, juice, or daily routines because it is simple and fridge-ready. We keep the wording honest and do not promise performance or medical results.",
  },
  {
    question: "Do you add fresh fruit to custom gels?",
    answer:
      "No. Shipped jars only get powdered or prepared botanicals. That keeps fermentation risk lower and delivery more predictable. Fruit can be mixed in at home; add finished gel to shakes, oats, yoghurt, coconut water, juice, or warm drinks you make yourself.",
  },
  {
    question: "Can I choose herbs?",
    answer:
      "Right now we focus on plain Gold and Purple sea moss gel. Custom blends are being simplified; explore mix-in ideas on the Mix-Ins page for education. Selected mix-ins may return later. Our 300 ml jar and trial pouches stay plain.",
  },
  {
    question: "Is sea moss suitable for everyone?",
    answer:
      "Sea moss contains iodine and is not right for every diet. If you are pregnant, breastfeeding, on medication, unsure, or managing health concerns, speak with a qualified healthcare professional before use.",
  },
  {
    question: "What if I have a thyroid condition?",
    answer:
      "Sea moss is naturally iodine-rich. If you have thyroid disease, take thyroid medication, or were told to watch iodine, speak with your clinician before adding sea moss gel to your routine.",
  },
  {
    question: "Do you sell dried sea moss in bags?",
    answer:
      "Our focus today is ready-to-use gel. Dried sea moss in sealed bags can be lighter to post. We may add formats later. Email us if that matters to you.",
  },
  {
    question: "How do you ship?",
    answer:
      "UK orders only. We pack gel like fresh food, insulated when it helps, and use carriers suited to UK delivery.",
  },
];

export default function FaqPage() {
  const faqLd = buildFaqPageJsonLd(faqs.map((f) => ({ question: f.question, answer: f.answer })));

  return (
    // TODO(future-polish): FAQ page layout/copy pass — keep answers accurate; tighten visual hierarchy later.
    <SiteShell theme="dark">
      <SEO
        title="FAQ | MossMatrix Sea Moss Gel"
        description="MossMatrix FAQ: UK-only shipping, prep times, gold vs purple, website pricing, how to use gel, fridge and freezer rules, herbs, iodine and thyroid notes. Straight answers with structured data."
        path="/faq"
        jsonLd={[faqLd]}
      />
      <ContentPageLayout
        eyebrow="FAQ"
        title="Straight answers, no miracle dust."
        subtitle="If it sounds medical, we point you to a clinician. If it is practical gel stuff, it is below."
      >
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start mb-12">
          <div className="lg:col-span-7 space-y-3 order-1">
            {faqs.map((f) => (
              <details
                key={f.question}
                className="group rounded-2xl border border-cream bg-white px-5 py-4 open:shadow-soft transition"
              >
                <summary className="font-sans font-medium text-base sm:text-lg text-forest cursor-pointer list-none flex items-center justify-between gap-3 leading-snug">
                  <span>{f.question}</span>
                  <span className="text-gold text-sm group-open:rotate-180 transition shrink-0">▼</span>
                </summary>
                <p className="mt-3 text-sm text-botanical/85 leading-relaxed border-t border-cream/80 pt-3">{f.answer}</p>
              </details>
            ))}
          </div>

          <aside className="lg:col-span-5 space-y-5 order-2 lg:sticky lg:top-28">
            <PremiumPlaceholder
              label="Product photo: jar on stone surface"
              caption="Freshly prepared. Keep refrigerated."
              className="min-h-[220px]"
            />
            <PremiumPlaceholder
              variant="video"
              label="Video: how to use sea moss gel"
              caption="Add a spoonful to drinks, oats, or cooled tea."
              className="min-h-[180px]"
            />
            <div className="rounded-2xl border border-cream bg-[#FAF7F0] p-6 shadow-soft">
              <h2 className="font-sans font-semibold text-lg text-forest flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-gold/90 shrink-0" strokeWidth={1.6} aria-hidden />
                <span>Why people trust the batch</span>
              </h2>
              <ul className="mt-4 space-y-3 text-sm text-botanical/85 list-none p-0 m-0">
                <li className="flex gap-2.5">
                  <MapPin className="w-4 h-4 text-gold/80 shrink-0 mt-0.5" strokeWidth={1.65} aria-hidden />
                  <span>UK delivery only</span>
                </li>
                <li className="flex gap-2.5">
                  <Sparkles className="w-4 h-4 text-gold/80 shrink-0 mt-0.5" strokeWidth={1.65} aria-hidden />
                  <span>Freshly prepared in the UK</span>
                </li>
                <li className="flex gap-2.5">
                  <Snowflake className="w-4 h-4 text-gold/80 shrink-0 mt-0.5" strokeWidth={1.65} aria-hidden />
                  <span>Refrigerated product</span>
                </li>
                <li className="flex gap-2.5">
                  <Clock className="w-4 h-4 text-gold/80 shrink-0 mt-0.5" strokeWidth={1.65} aria-hidden />
                  <span>
                    Usually about 1–3 working days from order to dispatch; at peak times kitchen scheduling can take up to 48 hours after checkout before
                    your batch is slotted
                  </span>
                </li>
                <li className="flex gap-2.5">
                  <SlidersHorizontal className="w-4 h-4 text-gold/80 shrink-0 mt-0.5" strokeWidth={1.65} aria-hidden />
                  <span>Plain Gold and Purple first — custom blends being simplified</span>
                </li>
              </ul>
            </div>
          </aside>
        </div>

        <div className="max-w-3xl rounded-2xl border border-gold/40 bg-cream/60 p-6 md:p-8 text-sm text-botanical/90 leading-relaxed">
          <h2 className="font-sans font-semibold text-xl text-forest flex items-center gap-2 flex-wrap">
            <AlertTriangle className="w-5 h-5 text-gold/90 shrink-0" strokeWidth={1.6} aria-hidden />
            <span>Iodine and safety note</span>
          </h2>
          <p className="mt-3">
            Sea moss and sea botanicals naturally contain iodine. If you are pregnant, breastfeeding, on medication, managing thyroid issues,
            or unsure, speak with a qualified healthcare professional. Same guidance as our general site disclaimer.
          </p>
        </div>

        <ConversionCtas />

        <p className="text-sm text-botanical/80 text-center mt-10">
          Still stuck?{" "}
          <Link to="/contact" className="underline hover:text-forest">
            Contact
          </Link>{" "}
          or browse{" "}
          <Link to="/learn" className="underline hover:text-forest">
            Learn
          </Link>
          .
        </p>
      </ContentPageLayout>
    </SiteShell>
  );
}
